const STATIC_ASSET_PATH_PATTERN =
    /^\/.*\.(?:avif|png|jpe?g|webp|svg|ico|apng|gif)$/i;

const ABSOLUTE_URL_PATTERN = /^(?:[a-z][a-z\d+\-.]*:)?\/\//i;
const STATIC_ASSET_BASE_PATH = '/web/static';

export function buildStaticAssetPath(...segments: string[]): string {
    return `/${segments.filter(Boolean).join('/')}`;
}

export function normalizeStaticAssetBaseUrl(baseUrl: string): string {
    const normalizedBaseUrl = baseUrl.trim().replace(/\/+$/, '');

    if (!normalizedBaseUrl) {
        return normalizedBaseUrl;
    }

    try {
        const url = new URL(normalizedBaseUrl);
        const normalizedPathname = url.pathname.replace(/\/+$/, '');

        if (!normalizedPathname || normalizedPathname === '/') {
            url.pathname = STATIC_ASSET_BASE_PATH;
        } else if (!normalizedPathname.endsWith(STATIC_ASSET_BASE_PATH)) {
            url.pathname = `${normalizedPathname}${STATIC_ASSET_BASE_PATH}`;
        }

        return url.toString().replace(/\/+$/, '');
    } catch {
        if (normalizedBaseUrl.endsWith(STATIC_ASSET_BASE_PATH)) {
            return normalizedBaseUrl;
        }

        return `${normalizedBaseUrl}${STATIC_ASSET_BASE_PATH}`;
    }
}

export function isStaticAssetPath(value: string): boolean {
    if (!value || ABSOLUTE_URL_PATTERN.test(value)) {
        return false;
    }

    return STATIC_ASSET_PATH_PATTERN.test(value);
}

export function getStaticAssetUrl(value: string): string {
    if (!isStaticAssetPath(value)) {
        return value;
    }

    const baseUrl = process.env.NEXT_PUBLIC_IMAGE_PUBLIC_BASE_URL?.trim();

    if (!baseUrl) {
        return value;
    }

    return `${normalizeStaticAssetBaseUrl(baseUrl)}${value}`;
}

export function mapStaticAssetUrlsDeep<T>(value: T): T {
    if (typeof value === 'string') {
        return getStaticAssetUrl(value) as T;
    }

    if (Array.isArray(value)) {
        return value.map((item) => mapStaticAssetUrlsDeep(item)) as T;
    }

    if (value && typeof value === 'object') {
        return Object.fromEntries(
            Object.entries(value).map(([key, nestedValue]) => [
                key,
                mapStaticAssetUrlsDeep(nestedValue),
            ]),
        ) as T;
    }

    return value;
}
